import type { LucideIcon } from 'lucide-react';
import dayjs from 'dayjs';
import { getName } from 'i18n-iso-countries';
import { Calendar, Calendar1, CalendarSync, CalendarX2, Globe, MapPin } from 'lucide-react';
import { CircleFlag } from 'react-circle-flags';

import type {
  DomainInfo as DomainInfoType,
  ServerLocation,
} from '@ballerine/digital-footprint-schemas';
import { NOT_DETECTED } from '@ballerine/common';

import { TextWithNAFallback } from '@/components/atoms/TextWithNAFallback';

type DomainInfoProps = {
  domainInfo?: DomainInfoType | null;
  reportCreatedAt?: Date | string | null;
};

type DetailItemProps = {
  icon: LucideIcon;
  title: string;
  value: string | React.ReactNode;
};

const DetailItem = ({ icon: Icon, title, value }: DetailItemProps) => {
  return (
    <div className="grid w-full grid-cols-[250px_1fr] items-center">
      <div className="flex min-w-0 items-center gap-2">
        <Icon className="h-4 w-4 text-zinc-500" />
        <p className="text-sm text-slate-400">{title}</p>
      </div>

      <TextWithNAFallback className="overflow-hidden text-ellipsis text-sm text-gray-800">
        {value}
      </TextWithNAFallback>
    </div>
  );
};

const calculateDomainAge = (
  creationDate: string | undefined | null,
  reportCreatedAt?: Date | string | null,
) => {
  if (!creationDate) {
    return NOT_DETECTED;
  }

  let diffDays = 0;

  try {
    const created = dayjs(creationDate);
    const reference = reportCreatedAt ? dayjs(reportCreatedAt) : dayjs();
    diffDays = reference.diff(created, 'day');
  } catch {
    return NOT_DETECTED;
  }

  return Intl.NumberFormat('en').format(diffDays) + ' days old';
};

const formatDate = (date: string | undefined | null) => {
  if (!date) {
    return NOT_DETECTED;
  }

  try {
    return dayjs(date).format('YYYY-MM-DD');
  } catch {
    return NOT_DETECTED;
  }
};

const renderServerLocation = (serverLocation?: ServerLocation | null) => {
  if (!serverLocation) {
    return NOT_DETECTED;
  }

  const countryCode = serverLocation.country?.toUpperCase();
  const isIsoAlpha2 = Boolean(countryCode && countryCode.length === 2);
  const countryName =
    isIsoAlpha2 && countryCode
      ? (getName(countryCode, 'en') ?? countryCode)
      : (serverLocation.country ?? undefined);
  const locationSegments = [serverLocation.city, serverLocation.region, countryName].filter(
    Boolean,
  );

  if (locationSegments.length === 0 && !serverLocation.ip) {
    return NOT_DETECTED;
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-gray-800">
      {isIsoAlpha2 && countryCode && (
        <CircleFlag countryCode={countryCode.toLowerCase()} className="size-4" />
      )}
      <span className="text-sm text-gray-800">
        {locationSegments.length > 0 ? locationSegments.join(', ') : serverLocation.ip}
      </span>
    </div>
  );
};

export const DomainInfo = ({ domainInfo, reportCreatedAt }: DomainInfoProps) => {
  if (!domainInfo) {
    return (
      <div className="flex items-center gap-2.5">
        <Globe className="h-4 w-4 text-gray-400" />
        <p className="text-sm text-gray-500">No domain information available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DetailItem
        icon={Calendar1}
        title="Domain Age"
        value={calculateDomainAge(domainInfo.createdAt, reportCreatedAt)}
      />
      <DetailItem
        icon={Calendar}
        title="Domain Creation Date"
        value={formatDate(domainInfo.createdAt)}
      />
      <DetailItem
        icon={CalendarX2}
        title="Domain Expiration Date"
        value={formatDate(domainInfo.expiresAt)}
      />
      <DetailItem
        icon={CalendarSync}
        title="Domain Update Date"
        value={formatDate(domainInfo.updatedAt)}
      />
      <DetailItem
        icon={MapPin}
        title="Server Location"
        value={renderServerLocation(domainInfo.serverLocation)}
      />
    </div>
  );
};
